import React, { useEffect, useState } from 'react';
import { RouteProps } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import LoginFlow from '../../components/LoginFlow/LoginFlow';
import ColorModeToggle from '../../components/ColorModeToggle/ColorModeToggle';
import { useTranslation } from 'react-i18next';
import CONFIG from '../../config/config';
import { Sparkles, Layers, LineChart } from 'lucide-react';
import flavioAs from "../../assets/images/testimonials/flavio-as.jpg";
import emilyKijowska from "../../assets/images/testimonials/emily-kijowska.jpg";
import outletSlubny24 from "../../assets/images/testimonials/outlet24.jpg";
import oneLoveBridal from "../../assets/images/testimonials/ilonajankowska.jpg";

type Testimonial = {
  text: string;
  author: string;
  role: string;
  image?: string;
};

const LoginPage: React.FC<RouteProps> = () => {
  const { t, i18n } = useTranslation();
  
  const QUOTES: Testimonial[] = [
    {
      text: t('testimonials.testimonial_1.text'),
      author: t('testimonials.testimonial_1.name'),
      role: t('testimonials.testimonial_1.role'),
      image: oneLoveBridal,
    },
    {
      text: t('testimonials.testimonial_2.text'),
      author: t('testimonials.testimonial_2.name'),
      role: t('testimonials.testimonial_2.role'),
      image: flavioAs,
    },
    {
      text: t('testimonials.testimonial_3.text'),
      author: t('testimonials.testimonial_3.name'),
      role: t('testimonials.testimonial_3.role'),
      image: emilyKijowska,
    },
    {
      text: t('testimonials.testimonial_4.text'),
      author: t('testimonials.testimonial_4.name'),
      role: t('testimonials.testimonial_4.role'),
      image: outletSlubny24,
    }
  ];
  
  const languages = CONFIG.SUPPORTED_LANGUAGES.map(l => {
    return {
      code: l.code,
      icon: <img src={l.icon} alt={l.code} className="w-5 h-5 rounded shadow border border-gray-200" onClick={() => i18n.changeLanguage(l.code)} />, // smaller and subtle
    }
  });

  const [quoteIdx, setQuoteIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setQuoteIdx((prev) => (prev + 1) % QUOTES.length);
        setFade(true);
      }, 1500);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-hidden z-0">
      <div className="absolute top-4 right-4 z-20">
        <ColorModeToggle />
      </div>
      <div className="flex flex-col lg:flex-row flex-1 min-h-screen relative z-10">
        <div className="w-full lg:w-1/2 flex flex-col justify-center items-center bg-background min-h-[70vh] py-12 relative overflow-hidden">
          <div className="w-full max-w-lg px-4 relative">
            <div className="flex flex-col items-center mb-8 pt-6">
              <span className="text-5xl font-bold text-darker-gold-600 mb-2 tracking-wide">eAteliers</span>
              <span className="text-gray-500 text-base font-medium">{t('login.slogan')}</span>
            </div>
            <LoginFlow />
            <div className="flex flex-col items-center mt-8 space-y-2">
              <span className="text-xs text-gray-400 mb-1">{t('login.choose_language')}</span>
              <div className="flex justify-center space-x-2">
                {languages.map((lang, index) => (
                  <div key={index} className="p-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer flex items-center justify-center">
                    {lang.icon}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 flex flex-col justify-center items-start px-4 lg:px-16 bg-as-pink-100 min-h-[70vh] py-12 relative overflow-hidden">
          <div className="max-w-lg mx-auto w-full z-10">
            <h2 className="text-3xl font-bold mb-4 text-foreground">{t('login.headline')}</h2>
            <p className="mb-8 text-foreground/90">{t('login.intro')}</p>
            <ul className="mb-8 space-y-8">
              <li className="flex items-start space-x-4">
                <div className="w-16 h-16 aspect-square flex items-center justify-center bg-as-pink-200 border border-as-pink-300 rounded-lg p-2">
                  <Sparkles className="w-full h-full object-contain text-foreground/60" />
                </div>
                <div>
                  <div className="font-bold text-lg text-foreground mb-1">{t('login.features.1')}</div>
                  <div className="text-foreground/80 text-base">{t('login.features.1.desc')}</div>
                </div>
              </li>
              <li className="flex items-start space-x-4">
                <div className="w-16 h-16 aspect-square flex items-center justify-center bg-as-pink-200 border border-as-pink-300 rounded-lg p-2">
                  <Layers className="w-full h-full object-contain text-foreground/60" />
                </div>
                <div>
                  <div className="font-bold text-lg text-foreground mb-1">{t('login.features.2')}</div>
                  <div className="text-foreground/80 text-base">{t('login.features.2.desc')}</div>
                </div>
              </li>
              <li className="flex items-start space-x-4">
                <div className="w-16 h-16 aspect-square flex items-center justify-center bg-as-pink-200 border border-as-pink-300 rounded-lg p-2">
                  <LineChart className="w-full h-full object-contain text-foreground/60" />
                </div>
                <div>
                  <div className="font-bold text-lg text-foreground mb-1">{t('login.features.3')}</div>
                  <div className="text-foreground/80 text-base">{t('login.features.3.desc')}</div>
                </div>
              </li>
            </ul>
            <div className="border-t border-foreground/30 pt-6 mt-6 min-h-[120px]">
              <blockquote className={`text-foreground/90 italic mb-4 transition-opacity duration-700 ${fade ? 'opacity-100' : 'opacity-0'}`}>{QUOTES[quoteIdx].text}</blockquote>
              <div className={`flex items-center space-x-3 transition-opacity duration-700 ${fade ? 'opacity-100' : 'opacity-0'}`}> 
                {QUOTES[quoteIdx].image ? (
                  <img
                    src={QUOTES[quoteIdx].image}
                    alt={QUOTES[quoteIdx].author}
                    className="w-10 h-10 rounded-full object-cover border border-foreground/30"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-foreground/30" />
                )}
                <div>
                  <div className="text-foreground font-semibold">{QUOTES[quoteIdx].author}</div>
                  <div className="text-foreground/80 text-sm">{QUOTES[quoteIdx].role}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;

